import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { DEFAULT_ROW_HEIGHT } from "./adapter";

// Keep the skeleton dimensions in sync with the loaded grid.
const ROW_MARKER_WIDTH_PX = 40;
const MIN_COLUMN_WIDTH_PX = 104;
// Only the first few rows show placeholder bars; the rows below stay empty so
// the skeleton reads as a quiet sheet instead of generated noise.
const FILLED_ROW_COUNT = 5;
const CELL_BAR_CLASSNAME = "w-3/5";

const SKELETON_TAB_BAR_CLASSNAMES = ["w-16", "w-12", "w-14"];

const getGridTemplateColumns = (columnCount: number) =>
  `${ROW_MARKER_WIDTH_PX}px repeat(${columnCount}, minmax(0, 1fr))`;

interface SheetLoadingStateProps {
  fillHeight?: boolean;
}

export const SheetLoadingState = ({
  fillHeight = false,
}: SheetLoadingStateProps) => (
  <Stack
    align="start"
    gap={0}
    className={cn(
      "w-full",
      "min-h-0 min-w-0 overflow-hidden rounded-xl border border-line bg-surface-raised",
      { "h-full": fillHeight },
    )}
    aria-busy="true"
  >
    <span className="sr-only">Loading spreadsheet</span>
    <FormulaBarSkeleton />
    <GridSkeleton fillHeight={fillHeight} />
    <SheetTabsSkeleton />
  </Stack>
);

const FormulaBarSkeleton = () => (
  <Stack
    direction="row"
    align="stretch"
    gap={0}
    className="h-9 w-full shrink-0 border-b border-line bg-surface-sunken"
  >
    <Stack
      align="start"
      justify="center"
      gap={0}
      className="w-24 shrink-0 border-r border-line px-3"
    >
      <Skeleton className="h-3.5 w-8" />
    </Stack>
    <Stack
      align="center"
      justify="center"
      gap={0}
      className="w-9 shrink-0 border-r border-line"
    >
      <Skeleton className="h-3.5 w-4" />
    </Stack>
    <Stack
      align="start"
      justify="center"
      gap={0}
      className="min-w-0 flex-1 px-3"
    >
      <Skeleton className="h-3.5 w-3/5 max-w-md" />
    </Stack>
    <Stack
      align="center"
      justify="center"
      gap={0}
      className="w-10 shrink-0 border-l border-line"
    >
      <Skeleton className="h-4 w-4" />
    </Stack>
  </Stack>
);

interface GridSkeletonProps {
  fillHeight: boolean;
}

const GridSkeleton = ({ fillHeight }: GridSkeletonProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(0);
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;

    const measure = () => {
      setColumnCount(
        Math.max(
          1,
          Math.floor(
            (body.clientWidth - ROW_MARKER_WIDTH_PX) / MIN_COLUMN_WIDTH_PX,
          ),
        ),
      );
      setRowCount(
        Math.max(1, Math.floor(body.clientHeight / DEFAULT_ROW_HEIGHT)),
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  const gridTemplateColumns = getGridTemplateColumns(columnCount);

  return (
    <Stack
      align="start"
      gap={0}
      className={cn("w-full", "min-w-0 bg-surface-raised", {
        "min-h-0 flex-1": fillHeight,
        "h-[600px]": !fillHeight,
      })}
    >
      <ColumnHeaderSkeleton
        columnCount={columnCount}
        gridTemplateColumns={gridTemplateColumns}
      />
      <Stack
        align="start"
        ref={bodyRef}
        gap={0}
        className="min-h-0 w-full flex-1"
      >
        {Array.from({ length: rowCount }, (_, row) => (
          <SheetRowSkeleton
            key={row}
            isFilled={row < FILLED_ROW_COUNT}
            columnCount={columnCount}
            gridTemplateColumns={gridTemplateColumns}
          />
        ))}
      </Stack>
    </Stack>
  );
};

interface ColumnHeaderSkeletonProps {
  columnCount: number;
  gridTemplateColumns: string;
}

const ColumnHeaderSkeleton = ({
  columnCount,
  gridTemplateColumns,
}: ColumnHeaderSkeletonProps) => (
  <Stack
    align="stretch"
    gap={0}
    style={{ gridTemplateColumns }}
    className="grid h-9 w-full shrink-0 border-b border-line bg-surface-sunken"
  >
    <Stack align="start" gap={0} className="border-r border-line" />
    {Array.from({ length: columnCount }, (_, column) => (
      <Stack
        align="start"
        key={column}
        justify="center"
        gap={0}
        className="border-r border-line px-3"
      >
        <Skeleton className="h-2.5 w-6" />
      </Stack>
    ))}
  </Stack>
);

interface SheetRowSkeletonProps {
  isFilled: boolean;
  columnCount: number;
  gridTemplateColumns: string;
}

const SheetRowSkeleton = ({
  isFilled,
  columnCount,
  gridTemplateColumns,
}: SheetRowSkeletonProps) => (
  <Stack
    align="stretch"
    gap={0}
    style={{ gridTemplateColumns, height: DEFAULT_ROW_HEIGHT }}
    className="grid w-full shrink-0 border-b border-line"
  >
    <Stack
      justify="center"
      align="center"
      gap={0}
      className="border-r border-line bg-surface-sunken"
    >
      <Skeleton className="h-2.5 w-3" />
    </Stack>
    {Array.from({ length: columnCount }, (_, column) => (
      <Stack
        align="start"
        key={column}
        justify="center"
        gap={0}
        className="border-r border-line px-3"
      >
        {isFilled && <Skeleton className={cn("h-3", CELL_BAR_CLASSNAME)} />}
      </Stack>
    ))}
  </Stack>
);

const SheetTabsSkeleton = () => (
  <Stack
    direction="row"
    align="stretch"
    gap={0}
    className="h-8 w-full shrink-0 overflow-hidden border-t border-line bg-surface-sunken"
  >
    <Stack
      align="center"
      justify="center"
      gap={0}
      className="w-10 shrink-0 border-r border-line"
    >
      <Skeleton className="h-4 w-4" />
    </Stack>
    {SKELETON_TAB_BAR_CLASSNAMES.map((barClassName, tabIndex) => (
      <Stack
        align="start"
        key={tabIndex}
        justify="center"
        gap={0}
        className={cn("shrink-0 border-r border-line px-4", {
          // The first tab reads as the active sheet, like the loaded tab list.
          "bg-selected": tabIndex === 0,
        })}
      >
        <Skeleton className={cn("h-3", barClassName)} />
      </Stack>
    ))}
  </Stack>
);
